import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Bell,
  Clock,
  ClipboardCheck,
  Calendar,
  AlertTriangle,
  FolderKanban,
  UserX,
  CheckCheck,
  Trash2,
  Search,
  Filter,
  Settings,
  ChevronRight,
  Mail,
  Smartphone,
  RefreshCw,
  X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIFICATION_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'timesheet_submitted', label: 'Timesheet Submitted' },
  { value: 'time_off_request', label: 'Time Off Request' },
  { value: 'employee_not_tracking', label: 'Not Tracking' },
  { value: 'timesheet_overdue', label: 'Timesheet Overdue' },
  { value: 'project_over_budget', label: 'Over Budget' },
  { value: 'escalation', label: 'Escalation' }
];

const DELIVERY_OPTIONS = [
  { value: 'in_app', label: 'In-App Only', icon: Smartphone },
  { value: 'email', label: 'Email Only', icon: Mail },
  { value: 'both', label: 'Both', icon: Bell }
];

export default function AdminNotifications() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Preferences dialog
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [employees, setEmployees] = useState([]);

  // Access check - Admin only (managers get redirected)
  useEffect(() => {
    if (user) {
      if (user.role === 'manager') {
        toast.error('Admin Notifications page is not available for managers');
        navigate('/admin/dashboard');
      } else if (user.role !== 'admin') {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      let url = `${API_URL}/api/admin/notifications/history?limit=100`;
      if (typeFilter !== 'all') url += `&type=${typeFilter}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      setNotifications(data.notifications || []);
      setStats({ total: data.total, unread: data.unread });
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [token, typeFilter]);

  // Fetch preferences
  const fetchPreferences = async () => {
    try {
      const [prefsRes, employeesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/notification-preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/notification-preferences/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      const prefsData = await prefsRes.json();
      const employeesData = await employeesRes.json();
      
      setPreferences(prefsData);
      setEmployees(employeesData);
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Mark as read
  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/admin/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/admin/notifications/mark-all-read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setStats(prev => ({ ...prev, unread: 0 }));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/admin/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const notif = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setStats(prev => ({
        total: prev.total - 1,
        unread: notif?.is_read ? prev.unread : Math.max(0, prev.unread - 1)
      }));
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  // Clear all read notifications
  const clearReadNotifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/notifications/clear-read`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setNotifications(prev => prev.filter(n => !n.is_read));
      setStats(prev => ({ total: prev.unread, unread: prev.unread }));
      toast.success(`${data.count} notification(s) deleted`);
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };

  // Save preferences
  const savePreferences = async () => {
    setSavingPrefs(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/notification-preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });
      const data = await res.json();
      setPreferences(data);
      toast.success('Preferences saved');
      setShowPreferences(false);
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  // Get icon for notification type
  const getIcon = (type) => {
    switch (type) {
      case 'timesheet_submitted': return <ClipboardCheck className="w-5 h-5" />;
      case 'time_off_request': return <Calendar className="w-5 h-5" />;
      case 'employee_not_tracking': return <Clock className="w-5 h-5" />;
      case 'timesheet_overdue': 
      case 'escalation': return <AlertTriangle className="w-5 h-5" />;
      case 'project_over_budget': return <FolderKanban className="w-5 h-5" />;
      default: return <Bell className="w-5 h-5" />;
    }
  };

  // Get icon background color
  const getIconBg = (type, priority) => {
    if (priority === 'urgent' || priority === 'high') {
      return 'bg-red-100 text-red-600';
    }
    switch (type) {
      case 'timesheet_submitted': return 'bg-blue-100 text-blue-600';
      case 'time_off_request': return 'bg-purple-100 text-purple-600';
      case 'employee_not_tracking': return 'bg-amber-100 text-amber-600';
      case 'timesheet_overdue': return 'bg-orange-100 text-orange-600';
      case 'project_over_budget': return 'bg-red-100 text-red-600';
      case 'escalation': return 'bg-rose-100 text-rose-600';
      default: return 'bg-slate-100 dark:bg-slate-700 text-slate-600';
    }
  };

  // Filter notifications
  const filteredNotifications = notifications.filter(n => {
    if (searchTerm && !n.title.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !n.message.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (activeTab === 'unread' && n.is_read) return false;
    return true;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-notifications-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-xl">
                <Bell className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              {t('notifications.title')}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {stats.unread > 0 ? `${stats.unread} ${t('notifications.unread')}` : t('notifications.allCaughtUp')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchPreferences(); setShowPreferences(true); }}
              data-testid="notification-settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Preferences
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchNotifications}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
              <p className="text-sm text-slate-500">Total Notifications</p>
            </CardContent>
          </Card>
          <Card className={stats.unread > 0 ? 'border-purple-200 dark:border-purple-500/30' : ''}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.unread}</p>
              <p className="text-sm text-slate-500">Unread</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {notifications.filter(n => n.type === 'timesheet_submitted').length}
              </p>
              <p className="text-sm text-slate-500">Timesheet Alerts</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {notifications.filter(n => n.type === 'time_off_request').length}
              </p>
              <p className="text-sm text-slate-500">Time Off Requests</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="notification-search"
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[180px]" data-testid="type-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {NOTIFICATION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2 ml-auto">
                {stats.unread > 0 && (
                  <Button variant="outline" size="sm" onClick={markAllAsRead} data-testid="mark-all-read-btn">
                    <CheckCheck className="w-4 h-4 mr-2" />
                    Mark All Read
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={clearReadNotifications} className="text-red-600 hover:text-red-700" data-testid="clear-read-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Read
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
            <TabsTrigger value="unread">
              Unread
              {stats.unread > 0 && (
                <Badge className="ml-2 bg-purple-50 dark:bg-purple-900/30 text-white">{stats.unread}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value={activeTab} className="mt-4">
            {/* Notifications List */}
            {filteredNotifications.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No notifications found</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {searchTerm ? 'Try adjusting your search' : 'You\'re all caught up!'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredNotifications.map(notification => (
                      <div
                        key={notification.id}
                        className={`flex items-start gap-4 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${
                          !notification.is_read ? 'bg-purple-50 dark:bg-purple-900/30/50 dark:bg-purple-900/5' : ''
                        }`}
                        data-testid={`notification-item-${notification.id}`}
                      >
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${getIconBg(notification.type, notification.priority)}`}>
                          {getIcon(notification.type)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className={`font-medium ${!notification.is_read ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                              {notification.title}
                            </p>
                            {notification.priority && notification.priority !== 'normal' && (
                              <Badge className={`text-[10px] ${
                                notification.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                                notification.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                'bg-slate-100 dark:bg-slate-700 text-slate-600'
                              }`}>
                                {notification.priority.toUpperCase()}
                              </Badge>
                            )}
                            {!notification.is_read && (
                              <div className="w-2 h-2 rounded-full bg-purple-50 dark:bg-purple-900/30" />
                            )}
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-xs text-slate-400">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                            </span>
                            {notification.action_url && (
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs text-purple-600"
                                onClick={() => navigate(notification.action_url)}
                              >
                                {notification.action_label || 'View'}
                                <ChevronRight className="w-3 h-3 ml-1" />
                              </Button>
                            )}
                            {notification.email_sent && (
                              <Badge variant="outline" className="text-[10px]">
                                <Mail className="w-3 h-3 mr-1" />
                                Email sent
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {!notification.is_read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => markAsRead(notification.id)}
                              className="h-8 text-xs"
                            >
                              Mark Read
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteNotification(notification.id)}
                            className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Preferences Dialog */}
      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Admin Notification Preferences
            </DialogTitle>
            <DialogDescription>
              Configure how you receive admin notifications
            </DialogDescription>
          </DialogHeader>

          {preferences && (
            <div className="space-y-6 py-4">
              {/* Notification Types */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-slate-900 dark:text-white">Notification Types</h3>
                
                {/* Timesheet Submitted */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <ClipboardCheck className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Timesheet Submitted</p>
                      <p className="text-xs text-slate-500">When an employee submits their timesheet</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select
                      value={preferences.timesheet_submitted_delivery}
                      onValueChange={(v) => setPreferences({ ...preferences, timesheet_submitted_delivery: v })}
                      disabled={!preferences.timesheet_submitted_enabled}
                    >
                      <SelectTrigger className="w-[130px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DELIVERY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Switch
                      checked={preferences.timesheet_submitted_enabled}
                      onCheckedChange={(v) => setPreferences({ ...preferences, timesheet_submitted_enabled: v })}
                    />
                  </div>
                </div>

                {/* Time Off Request */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Calendar className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Time Off Request</p>
                      <p className="text-xs text-slate-500">When an employee requests time off</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select
                      value={preferences.time_off_request_delivery}
                      onValueChange={(v) => setPreferences({ ...preferences, time_off_request_delivery: v })}
                      disabled={!preferences.time_off_request_enabled}
                    >
                      <SelectTrigger className="w-[130px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DELIVERY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Switch
                      checked={preferences.time_off_request_enabled}
                      onCheckedChange={(v) => setPreferences({ ...preferences, time_off_request_enabled: v })}
                    />
                  </div>
                </div>

                {/* Employee Not Tracking */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Clock className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Employee Not Tracking</p>
                      <p className="text-xs text-slate-500">Daily alert if employee hasn't tracked time</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="time"
                      value={preferences.employee_not_tracking_threshold}
                      onChange={(e) => setPreferences({ ...preferences, employee_not_tracking_threshold: e.target.value })}
                      className="w-[100px] h-8 text-xs"
                      disabled={!preferences.employee_not_tracking_enabled}
                    />
                    <Select
                      value={preferences.employee_not_tracking_delivery}
                      onValueChange={(v) => setPreferences({ ...preferences, employee_not_tracking_delivery: v })}
                      disabled={!preferences.employee_not_tracking_enabled}
                    >
                      <SelectTrigger className="w-[130px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DELIVERY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Switch
                      checked={preferences.employee_not_tracking_enabled}
                      onCheckedChange={(v) => setPreferences({ ...preferences, employee_not_tracking_enabled: v })}
                    />
                  </div>
                </div>

                {/* Timesheet Overdue */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Timesheet Overdue</p>
                      <p className="text-xs text-slate-500">When employees miss submission deadline</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select
                      value={preferences.timesheet_overdue_delivery}
                      onValueChange={(v) => setPreferences({ ...preferences, timesheet_overdue_delivery: v })}
                      disabled={!preferences.timesheet_overdue_enabled}
                    >
                      <SelectTrigger className="w-[130px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DELIVERY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Switch
                      checked={preferences.timesheet_overdue_enabled}
                      onCheckedChange={(v) => setPreferences({ ...preferences, timesheet_overdue_enabled: v })}
                    />
                  </div>
                </div>

                {/* Project Over Budget */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <FolderKanban className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Project Over Budget</p>
                      <p className="text-xs text-slate-500">When a project exceeds estimated hours</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select
                      value={preferences.project_over_budget_delivery}
                      onValueChange={(v) => setPreferences({ ...preferences, project_over_budget_delivery: v })}
                      disabled={!preferences.project_over_budget_enabled}
                    >
                      <SelectTrigger className="w-[130px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DELIVERY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Switch
                      checked={preferences.project_over_budget_enabled}
                      onCheckedChange={(v) => setPreferences({ ...preferences, project_over_budget_enabled: v })}
                    />
                  </div>
                </div>

                {/* Escalation */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-rose-100 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-rose-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Escalation Alert</p>
                      <p className="text-xs text-slate-500">Reminder for pending approvals over {preferences.escalation_hours}h</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      value={preferences.escalation_hours}
                      onChange={(e) => setPreferences({ ...preferences, escalation_hours: parseInt(e.target.value) || 48 })}
                      className="w-[70px] h-8 text-xs"
                      min={12}
                      max={168}
                      disabled={!preferences.escalation_enabled}
                    />
                    <span className="text-xs text-slate-500">hrs</span>
                    <Select
                      value={preferences.escalation_delivery}
                      onValueChange={(v) => setPreferences({ ...preferences, escalation_delivery: v })}
                      disabled={!preferences.escalation_enabled}
                    >
                      <SelectTrigger className="w-[130px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DELIVERY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Switch
                      checked={preferences.escalation_enabled}
                      onCheckedChange={(v) => setPreferences({ ...preferences, escalation_enabled: v })}
                    />
                  </div>
                </div>
              </div>

              {/* Digest Options */}
              <div className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-sm text-slate-900 dark:text-white">Digest Options</h3>
                
                {/* Daily Digest */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Daily Digest</p>
                    <p className="text-xs text-slate-500">Batch all notifications into one daily email</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="time"
                      value={preferences.daily_digest_time}
                      onChange={(e) => setPreferences({ ...preferences, daily_digest_time: e.target.value })}
                      className="w-[100px] h-8 text-xs"
                      disabled={!preferences.daily_digest_enabled}
                    />
                    <Switch
                      checked={preferences.daily_digest_enabled}
                      onCheckedChange={(v) => setPreferences({ ...preferences, daily_digest_enabled: v })}
                    />
                  </div>
                </div>

                {/* Weekly Summary */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Weekly Summary</p>
                    <p className="text-xs text-slate-500">Receive a weekly activity summary email</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select
                      value={preferences.weekly_summary_day}
                      onValueChange={(v) => setPreferences({ ...preferences, weekly_summary_day: v })}
                      disabled={!preferences.weekly_summary_enabled}
                    >
                      <SelectTrigger className="w-[110px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monday">Monday</SelectItem>
                        <SelectItem value="tuesday">Tuesday</SelectItem>
                        <SelectItem value="wednesday">Wednesday</SelectItem>
                        <SelectItem value="thursday">Thursday</SelectItem>
                        <SelectItem value="friday">Friday</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="time"
                      value={preferences.weekly_summary_time}
                      onChange={(e) => setPreferences({ ...preferences, weekly_summary_time: e.target.value })}
                      className="w-[100px] h-8 text-xs"
                      disabled={!preferences.weekly_summary_enabled}
                    />
                    <Switch
                      checked={preferences.weekly_summary_enabled}
                      onCheckedChange={(v) => setPreferences({ ...preferences, weekly_summary_enabled: v })}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreferences(false)}>
              Cancel
            </Button>
            <Button onClick={savePreferences} disabled={savingPrefs}>
              {savingPrefs ? 'Saving...' : 'Save Preferences'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
